import React from 'react';

import type { ExternalToast, PromiseData, PromiseT, ToastT, ToastToDismiss, ToastTypes } from './types';

let toastsCounter = 1;

const isHttpResponse = (data: any): data is Response =>
  data && typeof data === 'object' && 'ok' in data && typeof data.ok === 'boolean' && 'status' in data && typeof data.status === 'number';

class Observer {
  subscribers: Array<(toast: ExternalToast | ToastToDismiss) => void>;

  toasts: Array<ToastT | ToastToDismiss>;

  constructor() {
    this.subscribers = [];
    this.toasts = [];
  }

  // We use arrow functions to maintain the correct `this` reference
  subscribe = (subscriber: (toast: ToastT | ToastToDismiss) => void) => {
    this.subscribers.push(subscriber as never);

    return () => {
      const index = this.subscribers.indexOf(subscriber as never);

      this.subscribers.splice(index, 1);
    };
  };

  publish = (data: ToastT) => {
    this.subscribers.forEach(subscriber => subscriber(data));
  };

  addToast = (data: ToastT) => {
    this.publish(data);
    this.toasts = [...this.toasts, data];
  };

  create = (
    data: ExternalToast & {
      message?: string | React.ReactNode;
      type?: ToastTypes;
      promise?: PromiseT;
      jsx?: React.ReactElement;
    }
  ) => {
    const { message, ...rest } = data;
    const id = typeof data?.id === 'number' || (data.id?.length || 0) > 0 ? data.id : toastsCounter++;
    const alreadyExists = this.toasts.find(toast => toast.id === id);
    const dismissible = data.dismissible === undefined ? true : data.dismissible;

    if (alreadyExists) {
      this.toasts = this.toasts.map(toast => {
        if (toast.id === id) {
          this.publish({ ...toast, ...data, id, title: message });
          return {
            ...toast,
            ...data,
            id,
            dismissible,
            title: message
          };
        }

        return toast;
      });
    } else {
      this.addToast({ title: message, ...rest, dismissible, id: id! });
    }

    return id;
  };

  dismiss = (id?: number | string) => {
    if (!id) {
      this.toasts.forEach(toast => {
        this.subscribers.forEach(subscriber => subscriber({ id: toast.id, dismiss: true }));
      });
    }

    this.subscribers.forEach(subscriber => subscriber({ id, dismiss: true }));
    return id;
  };

  message = (message: string | React.ReactNode, data?: ExternalToast) => this.create({ ...data, message });

  error = (message: string | React.ReactNode, data?: ExternalToast) => this.create({ ...data, message, type: 'error' });

  success = (message: string | React.ReactNode, data?: ExternalToast) => this.create({ ...data, type: 'success', message });

  info = (message: string | React.ReactNode, data?: ExternalToast) => this.create({ ...data, type: 'info', message });

  warning = (message: string | React.ReactNode, data?: ExternalToast) => this.create({ ...data, type: 'warning', message });

  loading = (message: string | React.ReactNode, data?: ExternalToast) => this.create({ ...data, type: 'loading', message });

  promise = <ToastData>(promise: PromiseT<ToastData>, data?: PromiseData<ToastData>) => {
    if (!data) {
      // Nothing to show
      return;
    }

    let id: string | number | undefined;

    if (data.loading !== undefined) {
      id = this.create({
        ...data,
        promise,
        type: 'loading',
        message: data.loading,
        description: typeof data.description !== 'function' ? data.description : undefined
      });
    }

    const p = promise instanceof Promise ? promise : promise();

    let shouldDismiss = id !== undefined;
    let result: ['resolve', ToastData] | ['reject', unknown];

    const originalPromise = p
      .then(async response => {
        result = ['resolve', response];
        if (isHttpResponse(response) && !response.ok) {
          shouldDismiss = false;
          const message = typeof data.error === 'function' ? await data.error(`HTTP error! status: ${response.status}`) : data.error;
          const description = typeof data.description === 'function' ? await data.description(`HTTP error! status: ${response.status}`) : data.description;

          this.create({ id, type: 'error', message, description });
        } else if (data.success !== undefined) {
          shouldDismiss = false;
          const message = typeof data.success === 'function' ? await data.success(response) : data.success;
          const description = typeof data.description === 'function' ? await data.description(response) : data.description;

          this.create({ id, type: 'success', message, description });
        }
      })
      .catch(async error => {
        result = ['reject', error];
        if (data.error !== undefined) {
          shouldDismiss = false;
          const message = typeof data.error === 'function' ? await data.error(error) : data.error;
          const description = typeof data.description === 'function' ? await data.description(error) : data.description;

          this.create({ id, type: 'error', message, description });
        }
      })
      .finally(() => {
        if (shouldDismiss) {
          // Toast is still in load state (and will be indefinitely — dismiss it)
          this.dismiss(id);
          id = undefined;
        }

        data.finally?.();
      });

    const unwrap = () => new Promise<ToastData>((resolve, reject) => originalPromise.then(() => (result[0] === 'reject' ? reject(result[1]) : resolve(result[1]))).catch(reject));

    if (typeof id !== 'string' && typeof id !== 'number') {
      // cannot Object.assign on undefined
      // eslint-disable-next-line consistent-return
      return { unwrap };
    }

    // eslint-disable-next-line consistent-return
    return Object.assign(id, { unwrap });
  };

  custom = (jsx: (id: number | string) => React.ReactElement, data?: ExternalToast) => {
    const id = data?.id || toastsCounter++;

    this.create({ jsx: jsx(id), id, ...data });
    return id;
  };
}

export const ToastState = new Observer();

// bind this to the toast function
const toastFunction = (message: string | React.ReactNode, data?: ExternalToast) => {
  const id = data?.id || toastsCounter++;

  ToastState.addToast({
    title: message,
    ...data,
    id
  });
  return id;
};

const basicToast = toastFunction;

const getHistory = () => ToastState.toasts;

// We use `Object.assign` to maintain the correct types as we would lose them otherwise
export const toast = Object.assign(
  basicToast,
  {
    success: ToastState.success,
    info: ToastState.info,
    warning: ToastState.warning,
    error: ToastState.error,
    custom: ToastState.custom,
    message: ToastState.message,
    promise: ToastState.promise,
    dismiss: ToastState.dismiss,
    loading: ToastState.loading
  },
  { getHistory }
);
